import { Flex, type TableColumnType, Tag, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { formatNumber } from '@constants';
import { formatReferenceDay } from '@utils';

import DifferenceTag from './DifferenceTag';
import { transactionsLink } from './transactionsLink';

const { Text } = Typography;

export type DiscrepancyRow = Schemas.BalancePortfolioAccountDto;

/**
 * The diagnosis, in words. Two causes, two different jobs — and the wording has to make clear which
 * one needs chasing, because a booking-date shift reports a difference of nothing over the pair and
 * would otherwise read as "resolved itself, ignore".
 */
const shapes: Record<
  DiscrepancyRow['shape'],
  { label: string; colour: string; hint: string }
> = {
  booking_date_shift: {
    label: 'Date shift',
    colour: 'orange',
    hint: 'An interval next to this one is out by exactly the opposite amount, so the transaction is one we already hold — dated a day off. The money is accounted for; only the day it lands on is wrong.',
  },
  unexplained: {
    label: 'Unexplained',
    colour: 'red',
    hint: 'Nothing next to this cancels it out. The bank moved money we hold no record of — or posted a balance several days late, which the interval walk underneath will show as a run of differences that cancel further along.',
  },
};

/**
 * The whole walked window, not the interval the discrepancy started in.
 *
 * The row-level link answers the row-level question — "show me this account's transactions over the
 * period this is about" — and the period is the window, which is what every other figure on the row
 * except one is measured over. Filtering to the first failing interval made it a single day, which
 * reads as the page having lost the rest.
 *
 * Each interval keeps its own link inside the expanded walk, where the per-interval figures are. That
 * is the narrow question, and it belongs beside the narrow numbers.
 */
const windowOf = (row: DiscrepancyRow) => ({
  companyId: row.companyId,
  accountId: row.accountId,
  from: row.intervals[0]?.window.from ?? row.startedIn.from,
  to: row.intervals.at(-1)?.window.to ?? row.startedIn.to,
});

/**
 * The same diagnosis, said precisely when the cancelling half is the pre-window figure rather than the
 * interval after.
 *
 * The backend calls both a date shift, and rightly — same fault, same non-response, and a third enum
 * member would have to be learned by both portal clients. But "the interval straight after this one"
 * is not what happened here, and a tooltip that names the wrong neighbour is a diagnosis the reader
 * cannot check against the two figures on the row. Derived here because the row already carries both.
 */
const cancelledByOpening = (row: DiscrepancyRow): boolean =>
  Boolean(
    // The same two conditions the backend applies: the failure is the first interval there is, and the
    // pre-window figure is exactly its opposite. Without the first, a coincidence further down the walk
    // would be described as an edge shift.
    row.intervals[0] &&
      !row.intervals[0].matched &&
      Math.round(row.opening.difference * 1e4) ===
        -Math.round(row.startedWith * 1e4)
  );

export const getColumns = (): TableColumnType<DiscrepancyRow>[] => [
  {
    key: 'startedIn',
    title: 'Started in',
    width: 200,
    // The list arrives ordered oldest-first from the backend, and this is the column that ordering
    // is by — so it leads. How long a discrepancy has gone unnoticed is the only ordering that puts
    // the one most likely to have compounded at the top.
    render: (_, row) => (
      <Text>
        {formatReferenceDay(row.startedIn.from)} →{' '}
        {formatReferenceDay(row.startedIn.to)}
      </Text>
    ),
  },
  {
    key: 'company',
    title: 'Company',
    ellipsis: true,
    // Straight to that company's Accounts tab, which is where the per-account reconciliation and the
    // sync button are. A row that names a problem without a way to open it is a dead end.
    render: (_, row) => (
      <Link to={`/companies/${row.companyId}/accounts`}>{row.companyName}</Link>
    ),
  },
  {
    key: 'account',
    title: 'Account',
    ellipsis: true,
    /** Through to the transactions this account was checked against, over the whole walked window. */
    render: (_, row) => (
      <Flex align="center" gap={8}>
        <Tooltip
          title={`Open the transactions we hold for this account from ${formatReferenceDay(windowOf(row).from)} to ${formatReferenceDay(windowOf(row).to)} — the window these figures were computed over. Expand the row for a single interval.`}
        >
          <Link to={transactionsLink(windowOf(row))}>
            {row.accountName || '—'}
          </Link>
        </Tooltip>
        <Text type="secondary">{row.currencyCode}</Text>
      </Flex>
    ),
  },
  {
    key: 'shape',
    title: 'Diagnosis',
    width: 140,
    render: (_, row) => {
      const shape = shapes[row.shape];
      const acrossTheEdge =
        row.shape === 'booking_date_shift' && cancelledByOpening(row);

      return (
        <Tooltip
          title={
            acrossTheEdge
              ? `What was outstanding before the window is exactly the opposite of this interval, so the transaction is one we already hold: the bank dated it on or before ${formatReferenceDay(row.opening.upTo)} and we booked it just after. The money is accounted for; only the day it lands on is wrong.`
              : shape?.hint
          }
        >
          <Tag color={shape?.colour}>{shape?.label ?? row.shape}</Tag>
        </Tooltip>
      );
    },
  },
  {
    key: 'startedWith',
    title: 'In that interval',
    align: 'right',
    width: 150,
    /**
     * The first failing interval's own amount — the interval named two columns to the left, not the
     * account and not the window.
     *
     * It was headed "Off by", which named an amount without saying what was off by it, on a row that
     * carries three other amounts of the same kind over three other spans. The four now read as the
     * scopes they are: in that interval, across the window, before the window, overall.
     *
     * Not the chain total, which is zero for a date shift — "off by €0.00" on a row that is on this
     * list at all reads as a bug. Signed, because which way it went is what tells a later interval
     * that cancels it from one that compounds it.
     */
    render: (_, row) => (
      <DifferenceTag
        currencyCode={row.currencyCode}
        difference={row.startedWith}
        scope="How far the interval in the first column was out: the movement in the bank's own balance over those days, against the transactions we hold for them."
      />
    ),
  },
  {
    key: 'difference',
    title: 'Across the window',
    align: 'right',
    width: 160,
    /**
     * Every interval in the window added together. Zero means the discrepancies since have cancelled —
     * for a date shift that is the whole story, and for anything else it means the books have caught up
     * even though a day in between did not add up. Kept beside the interval's own figure rather than
     * instead of it, because they answer different questions and either alone is misread.
     *
     * A chain that totals nothing while four intervals in it failed is a bank that posted its balance
     * late, not an account that never went wrong — which is what the signs in the expanded walk show,
     * and what the tooltip points at.
     */
    render: (_, row) =>
      row.difference === 0 ? (
        <Tooltip title="Later intervals cancel it out — equal amounts the other way — so the chain as a whole adds up. The days in between still do not: expand the row to see which, and which way each went.">
          <Text type="secondary">settled</Text>
        </Tooltip>
      ) : (
        <Text>
          {formatNumber(row.difference, {
            style: 'currency',
            currency: row.currencyCode,
          })}
        </Text>
      ),
  },
  {
    key: 'opening',
    title: 'Before the window',
    align: 'right',
    width: 170,
    /**
     * What the walk assumed rather than checked, on the row rather than only inside it: everything
     * else on this line is a movement *within* the window, so widening the window would make an older
     * discrepancy appear here and narrowing it would make one vanish, with nothing on screen to say
     * that is what happened.
     *
     * Muted, and never red. For most accounts it is non-zero because our transaction history starts
     * later than the account does — the tooltip names the date it starts on, and without that date the
     * figure is unreadable rather than merely uncertain.
     */
    render: (_, row) => {
      const { difference, since, upTo } = row.opening;

      if (difference === 0) {
        return <Text type="secondary">adds up</Text>;
      }

      return (
        <Tooltip
          title={
            since
              ? `The balance on ${formatReferenceDay(upTo)} against every transaction we hold from ${formatReferenceDay(since)} onwards. Usually this is our history starting later than the account does, not something missing.`
              : `The balance on ${formatReferenceDay(upTo)}. We hold no transactions for this account before the window at all, so the whole opening balance is unaccounted for.`
          }
        >
          <Text type="secondary">
            {formatNumber(difference, {
              style: 'currency',
              currency: row.currencyCode,
              signDisplay: 'exceptZero',
            })}
          </Text>
        </Tooltip>
      );
    },
  },
  {
    key: 'overall',
    title: 'Overall',
    align: 'right',
    width: 140,
    /**
     * "Before the window" plus "Across the window" — how far out the account is once the part the walk
     * assumed is added to the part it checked.
     *
     * It exists because those two are routinely equal and opposite, and nothing on the row said so: a
     * balance the bank dated before the window's opening and we booked just after it appears as a
     * positive figure outside the window and a negative one inside it, which reads as two problems
     * where there is one transaction dated a day off. When they cancel, this column says so in words.
     *
     * Muted, never red, and it does not stand in for "is there a problem": zero here still means an
     * interval inside the window did not add up, and it inherits the opening's caveat — most of what it
     * adds is our transaction history starting later than the account does.
     */
    render: (_, row) => {
      // Summed in the backend's own scale (×10 000) and divided back out, rather than added as the
      // currency floats they arrive as. Binary addition of an exactly-opposite pair is exact, so the
      // zero below would hold either way — but that is a property of these two inputs, not of the test,
      // and the same test one term later (a third figure, a currency with three decimals) would not be
      // safe. Deciding it on integers is what the backend does, for the same reason.
      const units =
        Math.round(row.opening.difference * 1e4) +
        Math.round(row.difference * 1e4);
      const closesAt = row.intervals.at(-1)?.window.to;

      if (units === 0) {
        return (
          <Tooltip
            title={`What was outstanding before the window and what the walk found cancel exactly, so as of ${closesAt ? formatReferenceDay(closesAt) : 'the end of the window'} the balance and the transactions we hold agree. Most often a transaction the bank dated before the window and we booked just inside it — the money is accounted for, the day it lands on is not.`}
          >
            <Text type="secondary">nothing outstanding</Text>
          </Tooltip>
        );
      }

      return (
        <Tooltip title="Before the window plus across the window: how far out the account is in total, as far back as the transactions we hold reach.">
          <Text type="secondary">
            {formatNumber(units / 1e4, {
              style: 'currency',
              currency: row.currencyCode,
              signDisplay: 'exceptZero',
            })}
          </Text>
        </Tooltip>
      );
    },
  },
];
